import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../constants';
import { UserRole } from '../types';

interface ProfileProps {
  onNavigate: (page: string) => void;
}

const Profile: React.FC<ProfileProps> = ({ onNavigate }) => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: user?.email || '',
    businessName: user?.businessName || '',
    role: user?.role || 'CLINIC' as UserRole,
    contactName: user?.contactName || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    state: user?.state || '',
    zip: user?.zip || ''
  });

  const [isInitialized, setIsInitialized] = useState(!!user);

  // Initialize form when user data first becomes available
  React.useEffect(() => {
    if (user && !isInitialized) {
      setFormData({
        email: user.email || '',
        businessName: user.businessName || '',
        role: user.role || 'CLINIC',
        contactName: user.contactName || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip: user.zip || ''
      });
      setIsInitialized(true);
    }
  }, [user, isInitialized]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      console.log('Updating profile for user:', user.id, formData);
      const updatedUser = await api.updateUser(user.id, formData);
      console.log('API Response:', updatedUser);
      
      // Update global context by merging current state with server response
      // This ensures that even if server response is partial, we don't lose data
      const mergedUser = { ...user, ...formData, ...updatedUser };
      updateUser(mergedUser);
      
      // Update local form state from merged data
      setFormData({
          email: mergedUser.email || '',
          businessName: mergedUser.businessName || '',
          role: mergedUser.role || 'CLINIC',
          contactName: mergedUser.contactName || '',
          phone: mergedUser.phone || '',
          address: mergedUser.address || '',
          city: mergedUser.city || '',
          state: mergedUser.state || '',
          zip: mergedUser.zip || ''
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Update failed:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 flex justify-between items-end">
            <div>
                <h1 className="text-3xl font-black text-slate-900">Account Settings</h1>
                <p className="text-slate-500">Update your business profile and contact information</p>
            </div>
            <button
                onClick={async () => {
                    if (!user) return;
                    setLoading(true);
                    try {
                        const fresh = await api.getUser(user.id);
                        updateUser(fresh);
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 2000);
                    } catch (e) {
                        setError('Failed to refresh data');
                    } finally {
                        setLoading(false);
                    }
                }}
                className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-all"
            >
                🔄 Refresh Data
            </button>
        </div>

        {success && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 font-bold rounded-r-lg">
                ✅ Profile updated successfully!
            </div>
        )}

        {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 font-bold rounded-r-lg">
                ❌ {error}
            </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100">
            <form onSubmit={handleSubmit} className="p-8 space-y-8">
                {/* Login Info */}
                <section>
                    <h3 className="text-sm font-black uppercase text-slate-400 tracking-wider mb-4">Login Information</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Email Address</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                required
                            />
                            <p className="text-[10px] text-slate-400 mt-1">Changing your email will update your login credentials.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Internal ID</label>
                            <input
                                type="text"
                                value={user?.id}
                                disabled
                                className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 cursor-not-allowed"
                            />
                        </div>
                    </div>
                </section>

                {/* Business Info */}
                <section className="border-t border-slate-100 pt-8">
                    <h3 className="text-sm font-black uppercase text-slate-400 tracking-wider mb-4">Business Profile</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Business Name</label>
                            <input
                                type="text"
                                value={formData.businessName}
                                onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Business Type</label>
                            <select
                                value={formData.role}
                                onChange={(e) => setFormData({...formData, role: e.target.value as UserRole})}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                            >
                                <option value="CLINIC">Dental Clinic</option>
                                <option value="LAB">Dental Laboratory</option>
                            </select>
                        </div>
                    </div>
                </section>

                {/* Contact Info */}
                <section className="border-t border-slate-100 pt-8">
                    <h3 className="text-sm font-black uppercase text-slate-400 tracking-wider mb-4">Contact Details</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Primary Contact</label>
                            <input
                                type="text"
                                value={formData.contactName}
                                onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Phone Number</label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                                required
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-slate-700 mb-2">Street Address</label>
                            <input
                                type="text"
                                value={formData.address}
                                onChange={(e) => setFormData({...formData, address: e.target.value})}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">City</label>
                            <input
                                type="text"
                                value={formData.city}
                                onChange={(e) => setFormData({...formData, city: e.target.value})}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">State / Prov</label>
                                <input
                                    type="text"
                                    value={formData.state}
                                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">ZIP / Postal</label>
                                <input
                                    type="text"
                                    value={formData.zip}
                                    onChange={(e) => setFormData({...formData, zip: e.target.value})}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <div className="pt-6 border-t border-slate-100 flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={() => onNavigate('home')}
                        className="px-6 py-3 text-slate-500 font-bold hover:text-slate-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 text-white px-10 py-3 rounded-xl font-black hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 disabled:bg-slate-300 flex items-center"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving Changes...
                            </>
                        ) : 'Save Profile'}
                    </button>
                </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
